/**
 * 微信SDK工具函数
 * 职责：统一管理微信SDK初始化，避免URL不一致问题
 */

import { apiService } from '@/api/apiService.js'
import { WECHAT_URL_PARAMS, WECHAT_INIT_TIMEOUT, LOG_ICONS } from '@/constants/wechat.js'
import { getWechatSDKConfig, wechatOptions } from '@/config/wechat.js'
import { platformDetector } from '@/utils/platform.js'
import { unlockAudio } from '@/composables/useAudio.js'

// ==================== 状态管理 ====================

/** @type {boolean} SDK是否已就绪 */
let isReady = false

/** @type {boolean} 是否正在初始化 */
let isInitializing = false

/** @type {Promise<boolean>|null} 初始化Promise */
let initPromise = null

/** @type {string|null} 用于配置的URL */
let configUrl = null

// ==================== 私有函数 ====================

/**
 * 获取干净的URL（去除hash和微信参数）
 * Hash模式下，签名URL永远是 # 号前的部分，iOS和Android行为一致
 * @returns {string} 清理后的URL
 */
function getCleanUrl() {
  // Hash模式下，移除 # 及后面的所有内容
  let url = window.location.href.split('#')[0]

  try {
    const urlObj = new URL(url)

    // 移除微信相关参数（如果有的话）
    WECHAT_URL_PARAMS.forEach(param => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.delete(param)
        if (wechatOptions.verbose) {
          // debug(`${LOG_ICONS.CLEAN} 移除微信参数:`, param)
        }
      }
    })

    url = urlObj.toString()
  } catch (error) {
    // warn(`${LOG_ICONS.WARNING} URL解析失败:`, error)
  }

  return url
}

/**
 * 验证微信配置是否完整
 * @param {Object} config - 后端返回的配置
 * @returns {boolean} 是否有效
 */
function isValidConfig(config) {
  const requiredFields = ['appId', 'timestamp', 'nonceStr', 'signature']

  for (const field of requiredFields) {
    if (!config[field]) {
      // error(`${LOG_ICONS.ERROR} 微信配置缺少字段: ${field}`)
      return false
    }
  }

  return true
}

/**
 * 执行初始化
 * @returns {Promise<boolean>} 是否成功
 */
async function doInitialize() {
  try {
    // info(`${LOG_ICONS.START} 开始配置微信SDK`)
    // info(`${LOG_ICONS.PHONE} 当前完整URL:`, window.location.href)

    // Hash模式下，iOS和Android行为一致，不需要平台判断
    const platform = platformDetector.isIOS() ? 'iOS' : platformDetector.isAndroid() ? 'Android' : '其他'
    // info(`${LOG_ICONS.INFO} 设备平台:`, platform)

    // 获取签名URL（Hash模式下永远是 # 前的部分）
    const cleanUrl = getCleanUrl()
    configUrl = cleanUrl
    // info(`${LOG_ICONS.LINK} 用于签名的URL:`, cleanUrl)

    // 获取微信配置
    const signConfig = await apiService.getWechatConfig(cleanUrl)
    // info(`${LOG_ICONS.SUCCESS} 微信配置获取成功`)

    // 始终输出配置信息，方便调试
    // info('📋 微信配置详情:')
    // info('  AppId:', signConfig.appId)
    // info('  Timestamp:', signConfig.timestamp)
    // info('  NonceStr:', signConfig.nonceStr)
    // info('  Signature:', signConfig.signature)

    // 验证配置
    if (!isValidConfig(signConfig)) {
      // error(`${LOG_ICONS.ERROR} 微信配置不完整`)
      return false
    }

    // 配置微信SDK
    const sdkConfig = getWechatSDKConfig(signConfig)
    window.wx.config(sdkConfig)

    // info(`${LOG_ICONS.SUCCESS} 微信SDK配置已提交`)

    // 等待SDK初始化结果
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        // error(`${LOG_ICONS.ERROR} 微信SDK初始化超时`)
        isReady = false
        resolve(false)
      }, WECHAT_INIT_TIMEOUT)

      window.wx.ready(() => {
        clearTimeout(timeout)
        isReady = true
        // info(`${LOG_ICONS.SUCCESS} 微信SDK初始化成功`)
        resolve(true)
      })

      window.wx.error((res) => {
        clearTimeout(timeout)
        isReady = false
        // error(`${LOG_ICONS.ERROR} 微信SDK初始化失败:`, res.errMsg)
        // error('❌ 完整错误信息:', JSON.stringify(res))

        // 签名错误特殊提示
        if (res.errMsg && res.errMsg.includes('invalid signature')) {
          // error(`${LOG_ICONS.RED_DOT} 签名验证失败`)
          // error('前端URL:', cleanUrl)
          // error('请检查后端签名URL是否一致')
        }

        // 权限相关错误
        if (res.errMsg && res.errMsg.includes('permission')) {
          // error(`${LOG_ICONS.RED_DOT} 权限配置错误`)
          // error('请检查微信公众号后台的JS接口安全域名配置')
        }

        resolve(false)
      })
    })
  } catch (error) {
    // error(`${LOG_ICONS.ERROR} 微信SDK初始化异常:`, error)
    isReady = false
    return false
  }
}

// ==================== 公共API ====================

/**
 * 初始化微信SDK
 * @param {boolean} [force=false] - 是否强制重新初始化
 * @returns {Promise<boolean>} 是否成功
 */
export async function initWechat(force = false) {
  // 检查微信SDK是否加载
  if (!window.wx) {
    // error(`${LOG_ICONS.ERROR} 微信SDK未加载`)
    return false
  }

  // 已就绪，直接返回
  if (isReady && !force) {
    // debug(`${LOG_ICONS.SUCCESS} 微信SDK已就绪`)
    return true
  }

  // 正在初始化，等待完成
  if (isInitializing && initPromise) {
    // debug(`${LOG_ICONS.LOADING} 微信SDK初始化中...`)
    return initPromise
  }

  // 开始初始化
  isInitializing = true
  initPromise = doInitialize()

  const result = await initPromise
  isInitializing = false

  return result
}

/**
 * 检查微信SDK是否就绪
 * @returns {boolean} 是否就绪
 */
export function isWechatReady() {
  return isReady
}

/**
 * 获取配置时使用的URL
 * @returns {string|null} 配置URL
 */
export function getWechatConfigUrl() {
  return configUrl
}

/**
 * 重置微信SDK状态（用于测试）
 * @private
 */
export function resetWechatState() {
  isReady = false
  isInitializing = false
  initPromise = null
  configUrl = null
}

// 监听 WeixinJSBridgeReady 事件自动解锁音频
if (typeof window !== 'undefined') {
  document.addEventListener('WeixinJSBridgeReady', () => {
    // info('🔓 收到 WeixinJSBridgeReady 事件，尝试解锁音频')
    unlockAudio()
  }, false)
}

